import React, { type PropsWithChildren, useCallback, useMemo, useRef } from 'react';

import { SpotlightManager } from '@atlaskit/onboarding';
import {
	createFeatureGateHandler,
	createFrontendExperimentHandler,
	STATSIG_FULL_PAGE_SEARCH_LAYER,
} from '@atlassian/search-experiment';

import { SearchExperience } from '../../../common/constants/search';
import { AppContextProvider, type AppContextProviderProps } from '../../../common/ui/app-context';
import { ErrorComponent, type ErrorComponentProps } from '../../../common/ui/error';
import MessagesIntlProvider from '../../../common/ui/message-intl-provider/messages-intl-provider';
import { serveSearchPageRelayExp } from '../../../common/utils/experiments';
import { AdvancedSearchViewed } from '../../../controllers/analytics/advanced-search-viewed';
import { ErrorBoundary } from '../../../controllers/error-boundary';
import { FetchJsmIncidentFilterIds } from '../../../controllers/fetch-jsm-incident-filter-ids';
import Intercom from '../../../controllers/intercom';
import { RemoveReferrer } from '../../../controllers/remove-referrer';
import BootstrapProvider from '../../../controllers/store/bootstrap/bootstrap-provider';
import { type BootstrapContainerProps } from '../../../controllers/store/bootstrap/bootstrap-store-provider';
import { SearchStoreProvider } from '../../../controllers/store/search/search-store-provider';

import { FetchResults, RegisterHooksWithNewFilters } from './register-hooks';

type ShellProps = PropsWithChildren<BootstrapContainerProps & AppContextProviderProps>;

export const Shell = ({ children, ...props }: ShellProps): React.JSX.Element => {
	const {
		mapUserIdToProfileUrl,
		generateSearchUrl,
		queryParams,
		onNavigate,
		isNav4Enabled,
		isAdminHubAIEnabled,
		isRovoLLMEnabled,
		onSearchResultsShown,
		primaryProduct,
	} = props;

	const getFrontendExperiment = createFrontendExperimentHandler(
		props.features?.sideBySide?.experiments,
	);
	const getFrontendGate = createFeatureGateHandler(props.features?.sideBySide?.experiments);
	// hold on to the first function reference passed to the package to
	// avoid excessive re-rendering if the consumer doesn't pass in
	// a stable function
	const mapUserIdToProfileUrlRef = useRef(mapUserIdToProfileUrl);

	return (
		<BootstrapProvider
			{...props}
			statsigLayer={STATSIG_FULL_PAGE_SEARCH_LAYER}
			experience={SearchExperience.SEARCH_PAGE}
		>
			<SearchStoreProvider experience={SearchExperience.SEARCH_PAGE}>
				<AdvancedSearchViewed />
				<RemoveReferrer />
				<SpotlightManager blanketIsTinted={false}>
					<RegisterHooksWithNewFilters>
						<Intercom>
							<AppContextProvider
								mapUserIdToProfileUrl={mapUserIdToProfileUrlRef.current}
								generateSearchUrl={generateSearchUrl}
								queryParams={queryParams}
								onNavigate={onNavigate}
								getFrontendExperiment={getFrontendExperiment}
								getFeatureGate={getFrontendGate}
								isNav4Enabled={isNav4Enabled}
								isAdminHubAIEnabled={isAdminHubAIEnabled}
								isRovoLLMEnabled={isRovoLLMEnabled}
								onSearchResultsShown={onSearchResultsShown}
							>
								<FetchJsmIncidentFilterIds />
								{serveSearchPageRelayExp(primaryProduct, false, queryParams) ? null : (
									<FetchResults />
								)}
								{children}
							</AppContextProvider>
						</Intercom>
					</RegisterHooksWithNewFilters>
				</SpotlightManager>
			</SearchStoreProvider>
		</BootstrapProvider>
	);
};

const ShellWithErrorBoundary = (props: ShellProps): React.JSX.Element => {
	const { children } = props;

	const shell = useMemo(() => <Shell {...props}>{children}</Shell>, [children, props]);

	const renderWithErrorInfo = useCallback(
		(props: ErrorComponentProps) => <ErrorComponent {...props} />,
		[],
	);

	return (
		<ErrorBoundary renderWithErrorInfo={renderWithErrorInfo} section="SearchPageShell">
			<MessagesIntlProvider experience={SearchExperience.SEARCH_PAGE}>{shell}</MessagesIntlProvider>
		</ErrorBoundary>
	);
};

export default ShellWithErrorBoundary;
